import React, { useRef, useState, useEffect } from 'react';
import WaveSurfer from 'wavesurfer.js';
import SentimentAnalysis from '../charts/SentinmentAnalysis';
import {
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Skeleton,
} from '@mui/material';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import PauseCircleFilledIcon from '@mui/icons-material/PauseCircleFilled';
import Forward10Icon from '@mui/icons-material/Forward10';
import Replay10Icon from '@mui/icons-material/Replay10';
import Slider from '@mui/material/Slider';
import VolumeDown from '@mui/icons-material/VolumeDown';
import VolumeUp from '@mui/icons-material/VolumeUp';
import SpeedIcon from '@mui/icons-material/Speed';
import {
  ICallTranscriptionItem,
  ISentimentAnalysisResult,
  IUtterance,
  hhmmss,
} from './CallInfo';

import { CallTranscriptionInfoTooltip } from '../modals/CallTranscriptionModal';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import { useSelector, useDispatch } from 'react-redux';
import {
  toggleIsPlaying,
  updateMillisecond,
} from '../../store/callTranscriptionSlice';

const ContinuousSlider = (props: { volume: number; onVolumeChange: any }) => {
  return (
    <Grid container spacing={2} justifyContent="center" alignItems="center">
      <Grid item>
        <VolumeDown />
      </Grid>
      <Grid item xs>
        <div style={{ width: 75, display: 'flex' }}>
          <Slider
            value={props?.volume}
            onChange={props?.onVolumeChange}
            step={0.025}
            min={0.01}
            max={1}
            aria-labelledby="continuous-slider"
          />
        </div>
      </Grid>
      <Grid item>
        <VolumeUp />
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
    },
    audioContainer: {
      height: '38px',
      backgroundColor: 'rgba(233,233,233,.5)',
      borderRadius: '25px',
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      justifyContent: 'space-between',
      alignContent: 'center',
      alignItems: 'center',
    },
    paper: {
      padding: theme.spacing(2),
      textAlign: 'center',
      color: theme.palette.text.secondary,
    },
    audio: {
      transition: 'all 0.5s linear',
      boxShadow: '0px 0px 0px 0px',
      backgroundColor: '#f0f3f4',
      borderRadius: '7px 7px 7px 7px',
      height: 38,
      maxWidth: '-webkit-fill-available',
      margin: '0px auto',
      '&:focus': {
        outline: 0,
      },
    },
    svgIcon: {
      transform: 'scale(1.8)',
      color: 'rgba(178,178,178)',
    },
    svgIcon2: {
      color: '#0098C7',
    },
    timeSection: {
      fontSize: '13px',
      fontWeight: 100,
      color:
        theme.palette.mode === 'light'
          ? 'rgba(0, 0, 0, 0.54)'
          : 'rgba(255, 255, 255, 0.64)',
    },
    flexRowCenter: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    hoverDiv: {
      display: 'flex',
      padding: '8px',
      flexDirection: 'column',
      maxHeight: '200px',
      maxWidth: '200px',
      zIndex: 1550,
      backgroundColor:
        theme.palette.mode === 'light'
          ? 'rgba(255, 255, 255, 0.9)'
          : 'rgba(0, 0, 0, 0.54)',
      border: '1px solid rgba(233,233,233,.7)',
      borderRadius: '4px',
    },
  })
);

const ScrollAudioRow = (props: {
  speakerColorMap: Record<string, string>;
  item: ISentimentAnalysisResult | IUtterance;
  currMillisecond: number;
  callType: string;
}) => {
  const { speakerColorMap, item, currMillisecond } = props;
  const fieldRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (
      fieldRef.current &&
      Number(currMillisecond) > Number(item.start) &&
      Number(currMillisecond) < Number(item.end)
    ) {
      fieldRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'start',
      });
    }
  }, [currMillisecond, item]);
  return (
    <Grid container direction="row" spacing={1} ref={fieldRef}>
      <Grid item xs={3} sm={3} md={3} lg={3} xl={3}>
        <Grid
          container
          alignContent="flex-end"
          direction="column"
          style={{
            fontWeight: 'bold',
            color: speakerColorMap[item?.speaker],
          }}
        >
          {item?.speaker} |
        </Grid>
      </Grid>
      <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
        <Grid
          container
          direction="column"
          style={{
            fontWeight:
              Number(currMillisecond) > Number(item.start) &&
              Number(currMillisecond) < Number(item.end)
                ? 'bold'
                : 'normal',
          }}
        >
          "{item?.text}"
        </Grid>
      </Grid>
    </Grid>
  );
};

const FullCallTranscription = (props: {
  loading: boolean;
  item: ICallTranscriptionItem;
  callDate: string | undefined;
  callType: string;
  recordingUrl: string;
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const cTState = useSelector((state) => state?.callTranscription);
  const [volume, setVolume] = useState(0.25);
  const [, setSpeed] = useState(1);
  const [speakerColorMap, setSpeakerColorMap] = useState({});
  const speeds = [0.5, 0.75, 1.0, 1.5, 2.0];
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const { loading, item, callType, recordingUrl } = props;
  const speakerColors = React.useMemo(
    () =>
      callType === 'incoming'
        ? [
            '#3679a2',
            '#7ad3eb',
            '#234e69',
            '#4ec4e4',
            '#306b8f',
            '#64cce8',
            '#3c87b5',
            '#90daee',
            '#5c9fc9',
            '#4994c2',
            '#bde9f5',
          ]
        : [
            '#7ad3eb',
            '#3679a2',
            '#4ec4e4',
            '#234e69',
            '#64cce8',
            '#306b8f',
            '#90daee',
            '#3c87b5',
            '#4994c2',
            '#5c9fc9',
            '#bde9f5',
          ],
    [callType]
  );

  const waveformRef = useRef(null);
  const [offset, setOffset] = useState({ top: '0px', left: '0px' });
  const [showHoverDiv, setShowHoverDiv] = useState(false);
  const [hoverTime, setHoverTime] = useState('00:00');
  const [hoverSentiment, setHoverSentiment] = useState<
    ISentimentAnalysisResult
  >({
    confidence: 0,
    end: 0,
    sentiment: '',
    speaker: '',
    start: 0,
    text: '',
  });

  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    setSpeed(speeds[event.currentTarget.value]);
    waveformRef.current.setPlaybackRate(
      speeds[event.currentTarget.value],
      true
    );
    setAnchorEl(null);
  };
  useEffect(() => {
    const speakerMap = {};
    if (item?.speakers?.length) {
      item?.speakers?.forEach((u, i) => {
        speakerMap[u] = speakerColors[i];
      });
    }
    setSpeakerColorMap(speakerMap);
  }, [item]);

  useEffect(() => {
    if (window && document && item && recordingUrl) {
      const audio = new Audio();
      audio.controls = true;
      audio.src = recordingUrl;
      waveformRef.current = WaveSurfer?.create({
        container: '#waveform',
        barWidth: 8,
        cursorWidth: 2,
        cursorColor: 'red',
        splitChannels: true,
        splitDisplay: true,
        url: recordingUrl,
        splitChannelsOptions: {
          overlay: true,
          channelColors: {
            0: {
              progressColor:
                callType.toLowerCase() === 'incoming' ? '#4aabc5' : '#cbedf7',
              waveColor:
                callType.toLowerCase() === 'incoming' ? '#3679a2' : '#7ad3eb',
            },
            1: {
              progressColor:
                callType.toLowerCase() === 'incoming' ? '#cbedf7' : '#4aabc5',

              waveColor:
                callType.toLowerCase() === 'incoming' ? '#7ad3eb' : '#3679a2',
            },
          },
        },
        xhr: {
          cache: 'default',
          // mode: "no-cors",
          method: 'GET',
          credentials: 'same-origin',
          redirect: 'follow',
          referrer: 'client',
          headers: [
            { key: 'cache-control', value: 'no-cache' },
            { key: 'pragma', value: 'no-cache' },
            { key: 'access-control-allow-origin', value: '*' },
          ],
        },
      });
      waveformRef.current?.load(recordingUrl);
      waveformRef.current?.on('ready', () => {
        // waveformRef.current = wavesurfer;
        if (waveformRef.current) {
          waveformRef.current.setVolume(volume);
          setVolume(volume);
          if (
            waveformRef?.current &&
            cTState?.isPlaying &&
            cTState?.millisecond > 0
          ) {
            waveformRef.current.setCurrentTime(cTState.millisecond / 1000);
            waveformRef.current.playPause();
          }
        }
      });
      waveformRef?.current?.on('audioprocess', function () {
        const currTick = Math.round(
          waveformRef?.current?.getCurrentTime() * 1000
        );
        dispatch(updateMillisecond(currTick));
        if (currTick >= item?.totalDuration) {
          // toggleIsPlaying(false);
          dispatch(toggleIsPlaying(false));
        }
      });
      // Custom drawbars function
      // Note: this is based on the source code from the wavesurfer github
      // project, but with a few changes to the code that calls
      // wavesurfer.drawer.fillRect
      const customDrawBars = (wavesurfer, peaks, channelIndex, start, end) => {
        return wavesurfer.drawer.prepareDraw(
          peaks,
          channelIndex,
          start,
          end,
          ({
            absmax,
            hasMinVals,
            height,
            offsetY,
            halfH,
            peaks,
            channelIndex: ch,
          }) => {
            // if drawBars was called within ws.empty we don't pass a start and
            // don't want anything to happen
            if (start === undefined) {
              return;
            }

            // Skip every other value if there are negatives.
            const peakIndexScale = hasMinVals ? 2 : 1;
            const length = peaks.length / peakIndexScale;
            const bar =
              wavesurfer.params.barWidth * wavesurfer.params.pixelRatio;
            const gap =
              wavesurfer.params.barGap === null
                ? Math.max(wavesurfer.params.pixelRatio, ~~(bar / 2))
                : Math.max(
                    wavesurfer.params.pixelRatio,
                    wavesurfer.params.barGap * wavesurfer.params.pixelRatio
                  );
            const step = bar + gap;

            const scale = length / wavesurfer.drawer.width;
            const first = start;
            const last = end;
            let peakIndex = first;
            for (peakIndex; peakIndex < last; peakIndex += step) {
              // search for the highest peak in the range this bar falls into
              let peak = 0;
              let peakIndexRange =
                Math.floor(peakIndex * scale) * peakIndexScale; // start index
              const peakIndexEnd =
                Math.floor((peakIndex + step) * scale) * peakIndexScale;
              do {
                // do..while makes sure at least one peak is always evaluated
                const newPeak = Math.abs(peaks[peakIndexRange]); // for arrays starting with negative values
                if (newPeak > peak) {
                  peak = newPeak; // higher
                }
                peakIndexRange += peakIndexScale; // skip every other value for negatives
              } while (peakIndexRange < peakIndexEnd);

              // calculate the height of this bar according to the highest peak found
              let h = Math.round((peak / absmax) * halfH);

              // raise the bar height to the specified minimum height
              // Math.max is used to replace any value smaller than barMinHeight (not just 0) with barMinHeight
              if (wavesurfer.params.barMinHeight) {
                h = Math.max(h, wavesurfer.params.barMinHeight);
              }

              wavesurfer.drawer.fillRect(
                peakIndex + 0.5 / wavesurfer.params.pixelRatio, // halfPixel,
                ch === 1 ? halfH - h + offsetY : halfH + offsetY,
                bar + 0.5 / wavesurfer.params.pixelRatio, // halfPixel,
                h, //* 2,
                wavesurfer.params.barRadius || 0, // waveformRef.current.barRadius,
                ch
              );
            }
          }
        );
      };
      // Override the renderer
      if (waveformRef?.current) {
        waveformRef.current.drawer.drawBars = function (
          peaks,
          channelIndex,
          start,
          end
        ) {
          return customDrawBars(
            waveformRef.current,
            peaks,
            channelIndex,
            start,
            end
          );
        };
      }
    }
    return () => {
      waveformRef.current?.destroy();
    };
  }, [item, callType, recordingUrl]);

  return (
    <Grid container spacing={1} wrap="wrap">
      <Grid item xs={12}>
        {loading || !item ? (
          <Skeleton
            variant="rectangular"
            style={{ borderRadius: '25px' }}
            height={38}
          />
        ) : (
          <div className={classes.audioContainer}>
            <div style={{ color: 'rgba(178,178,178)', padding: '14px' }}>
              <PeopleAltOutlinedIcon color="inherit" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <div>
                  <IconButton
                    size="medium"
                    onClick={handleClick}
                    aria-expanded={open ? 'true' : undefined}
                    aria-haspopup="true"
                    aria-controls={open ? 'demo-positioned-menu' : undefined}
                    id="demo-positioned-button"
                  >
                    <SpeedIcon
                      fontSize="inherit"
                      className={classes.svgIcon2}
                    />
                  </IconButton>

                  <Menu
                    id="demo-positioned-menu"
                    aria-labelledby="demo-positioned-button"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                  >
                    {speeds.map((el, i) => (
                      <MenuItem key={el} value={i} onClick={handleClose}>
                        {el}
                      </MenuItem>
                    ))}
                  </Menu>
                </div>
                <IconButton
                  size="medium"
                  onClick={() => {
                    const curr = waveformRef?.current?.getCurrentTime();
                    const newTime = curr < 10 ? 0 : curr - 10;
                    waveformRef?.current?.setCurrentTime(newTime);
                  }}
                >
                  <Replay10Icon
                    fontSize="inherit"
                    className={classes.svgIcon2}
                  />
                </IconButton>
                <IconButton
                  size="medium"
                  onClick={() => {
                    waveformRef?.current?.playPause();
                    dispatch(
                      toggleIsPlaying(
                        waveformRef?.current?.isPlaying() ?? false
                      )
                    );
                  }}
                >
                  {cTState?.isPlaying ? (
                    <PauseCircleFilledIcon
                      className={classes.svgIcon2}
                      fontSize="inherit"
                    />
                  ) : (
                    <PlayCircleFilledIcon
                      fontSize="inherit"
                      className={classes.svgIcon2}
                    />
                  )}
                </IconButton>
                <IconButton
                  size="medium"
                  onClick={() => {
                    const curr = waveformRef?.current?.getCurrentTime();
                    const sec = waveformRef?.current?.getDuration();
                    const newTime = curr + 10 > sec ? sec : curr + 10;
                    waveformRef?.current?.setCurrentTime(newTime);
                  }}
                >
                  <Forward10Icon
                    fontSize="inherit"
                    className={classes.svgIcon2}
                  />
                </IconButton>
                <ContinuousSlider
                  volume={volume}
                  onVolumeChange={(e, newValue) => {
                    const newVolume = +newValue;
                    if (newVolume) {
                      setVolume(newVolume);
                      waveformRef.current.setVolume(newVolume || 1);
                    }
                  }}
                />
              </div>
            </div>
            <div
              style={{
                marginRight: '24px',
                display: 'flex',
                flexDirection: 'row',
                gap: '8px',
              }}
            >
              <div>Download: </div>
              <div>
                <Tooltip placement="right" title="Download Audio">
                  <a
                    href={recordingUrl}
                    target="_blank"
                    rel="noreferrer"
                    download={recordingUrl}
                  >
                    <div style={{ color: 'rgba(178,178,178)' }}>
                      <PlayCircleFilledIcon color="inherit" />
                    </div>
                  </a>
                </Tooltip>
              </div>
              <CSVLink
                filename={`Kixie-${moment(
                  item?.calldate ?? props?.callDate
                ).format('YYYY-MM-DD')}-call-transcription`}
                data={item?.sentimentAnalysisResults ?? ''}
              >
                <Tooltip
                  placement="right"
                  title="Download Full Audio Transcription Text"
                >
                  <div style={{ color: 'rgba(178,178,178)' }}>
                    <FileCopyOutlinedIcon color="inherit" />
                  </div>
                </Tooltip>
              </CSVLink>
            </div>
          </div>
        )}
      </Grid>
      <Grid container direction="row">
        <Grid item xs={12}>
          <div
            style={{ padding: '12px' }}
            onMouseLeave={() => {
              setShowHoverDiv(false);
              setOffset({ top: '0px', left: '0px' });
              setHoverSentiment({
                confidence: 0,
                end: 0,
                sentiment: '',
                speaker: '',
                start: 0,
                text: '',
              });
            }}
          >
            {showHoverDiv && (
              <div
                id="hoverDiv"
                className={classes.hoverDiv}
                style={{
                  position: 'fixed',
                  ...offset,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <div
                    style={{
                      height: '8px',
                      width: '8px',
                      backgroundColor: speakerColorMap[hoverSentiment?.speaker],
                    }}
                  ></div>
                  <div>
                    <strong>{hoverSentiment?.speaker ?? ''}</strong>
                  </div>
                </div>
                <div>
                  Duration: <strong>{hoverTime}</strong>
                </div>
                <div>
                  Total Duration: <strong>{hhmmss(item?.totalDuration)}</strong>
                </div>
              </div>
            )}
            {loading ? (
              <Skeleton variant="rectangular" height={127} />
            ) : (
              <div
                style={{ cursor: 'pointer' }}
                onMouseMove={(e) => {
                  setOffset({
                    top: e.clientY + 'px',
                    left: e.clientX + 10 + 'px',
                  });
                  const hoverTime =
                    waveformRef.current.drawer.handleEvent(e, true) *
                    waveformRef.current.getDuration();
                  const hoverMill = hoverTime * 1000;
                  const hoverSentiment = item?.sentimentAnalysisResults?.filter(
                    (el: ISentimentAnalysisResult, i) => {
                      return el.start < hoverMill && el.end > hoverMill;
                    }
                  )?.[0];
                  setHoverSentiment(hoverSentiment);
                  setHoverTime(hhmmss(hoverMill));
                  setShowHoverDiv(true);
                }}
                id="waveform"
              ></div>
            )}
          </div>
        </Grid>
      </Grid>
      <Grid container spacing={1} direction="row" wrap="wrap">
        <Grid item xs={3} sm={3} md={3} lg={3} xl={3}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              padding: '12px',
              height: '100%',
              justifyContent: 'space-around',
              marginLeft: '8px',
            }}
          >
            <div>
              <div
                className={classes.flexRowCenter}
                style={{
                  justifyContent: 'start',
                  gap: '12px',
                }}
              >
                <div
                  style={{
                    height: '14px',
                    width: '14px',
                    backgroundColor: '#7ad3eb',
                  }}
                ></div>
                <div>
                  <div
                    className={classes.flexRowCenter}
                    style={{
                      gap: '4px',
                    }}
                  >
                    <div>Agent Monologue</div>
                    <CallTranscriptionInfoTooltip
                      title={
                        'Shows the longest time the Agent party was consecutively talking on the call - with very little/no feedback from the Customer party during that duration'
                      }
                    />
                  </div>
                  <div className={classes.timeSection}>
                    {hhmmss(item?.agentMonologue)}
                  </div>
                </div>
              </div>
              <div
                className={classes.flexRowCenter}
                style={{
                  justifyContent: 'start',
                  gap: '12px',
                }}
              >
                <div
                  style={{
                    height: '14px',
                    width: '14px',
                    backgroundColor: '#3679a2',
                  }}
                ></div>
                <div>
                  <div
                    className={classes.flexRowCenter}
                    style={{
                      gap: '4px',
                    }}
                  >
                    <div>Customer Story</div>
                    <CallTranscriptionInfoTooltip
                      title={
                        'Shows the longest time the Customer party was consecutively talking on the call - with very little/no feedback from the Agent party during that duration'
                      }
                    />
                  </div>
                  <div className={classes.timeSection}>
                    {hhmmss(item?.contactStory)}
                  </div>
                </div>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                marginLeft: '26px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <div>Patience</div>
                <CallTranscriptionInfoTooltip
                  title={
                    'Shows the average duration that the Agent party will wait for a response from the Customer party - a higher average wait duration indicates better patience'
                  }
                />
              </div>
              <div className={classes.timeSection}>
                {hhmmss(props?.item?.patience)}
              </div>
            </div>
          </div>
        </Grid>
        <Grid
          item
          xs={9}
          sm={9}
          md={9}
          lg={9}
          xl={9}
          style={{
            maxHeight: '200px',
            overflow: 'scroll',
          }}
        >
          {(item?.sentimentAnalysisResults ?? item?.utterances ?? []).map(
            (item, i) => (
              <ScrollAudioRow
                key={i}
                speakerColorMap={speakerColorMap}
                item={item}
                currMillisecond={cTState?.millisecond}
                callType={callType}
              />
            )
          )}
        </Grid>
      </Grid>
      <Grid container direction="row">
        <Grid item xs={12}>
          <div style={{ padding: '12px' }}>
            <SentimentAnalysis
              millisecond={cTState?.millisecond}
              totalDuration={item?.totalDuration}
              item={item}
              loading={loading}
            />
          </div>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default FullCallTranscription;
